import { useEffect, useState, type ReactNode } from 'react';
import {
  Checkbox,
  DatePicker,
  Flex,
  Form,
  InputNumber,
  Modal,
  Radio,
  Select,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import type {
  AssetCommandDefinition,
  AssetCommandRequest,
  AssetGetConfigurationKey,
  AssetResetType,
  AssetTriggerMessage,
} from '../types';
import {
  GET_CONFIGURATION_KEYS,
  TRIGGER_MESSAGES,
  configurationKeyLabelKey,
  isPositiveIntegerString,
  isValidOcppHeartbeatIntervalValue,
  OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS,
  OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS,
  resetTypeLabelKey,
} from '../utils';
import {
  COMMAND_EXECUTE_ACCENT,
  COMMAND_EXECUTE_ACCENT_HOVER,
} from '../styles';
import styles from './AssetCommandForm.module.css';

type ExecuteFn = (request: AssetCommandRequest) => Promise<unknown>;

type FormProps = {
  command: AssetCommandDefinition;
  execute: ExecuteFn;
  commandsLocked: boolean;
  isActive: boolean;
  heartbeatInterval: string | null;
  onHeartbeatIntervalChange: (value: string | null) => void;
};

export function AssetCommandForm(props: FormProps) {
  switch (props.command.id) {
    case 'Reset':
      return <ResetCommandForm {...props} />;
    case 'ClearCache':
      return <ConfirmOnlyCommandForm {...props} confirmKind="clearCache" />;
    case 'GetLocalListVersion':
      return <SimpleCommandForm {...props} />;
    case 'GetConfiguration':
      return <GetConfigurationForm {...props} />;
    case 'TriggerMessage':
      return <TriggerMessageForm {...props} />;
    case 'UpdateHeartbeat':
      return <UpdateHeartbeatForm {...props} />;
    case 'GetDiagnostics':
      return <GetDiagnosticsForm {...props} />;
    default:
      return null;
  }
}

function CommandHeader({ command }: { command: AssetCommandDefinition }) {
  const { t } = useTranslation('assets');
  return (
    <div>
      <Typography.Text strong className={styles.headerTitle}>
        {t(command.titleKey)}
      </Typography.Text>
      <Typography.Paragraph className={styles.headerDescription}>
        {t(command.descriptionKey)}
      </Typography.Paragraph>
    </div>
  );
}

function CommandExecuteButton({
  label,
  loading,
  disabled,
  onClick,
}: {
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="primary"
      size="large"
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      className={styles.executeButton}
      style={{
        background: COMMAND_EXECUTE_ACCENT,
        borderColor: COMMAND_EXECUTE_ACCENT,
      }}
      onMouseEnter={(event) => {
        if (!disabled) {
          event.currentTarget.style.background = COMMAND_EXECUTE_ACCENT_HOVER;
          event.currentTarget.style.borderColor = COMMAND_EXECUTE_ACCENT_HOVER;
        }
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = COMMAND_EXECUTE_ACCENT;
        event.currentTarget.style.borderColor = COMMAND_EXECUTE_ACCENT;
      }}
    >
      {label}
    </Button>
  );
}

function CommandFormShell({
  command,
  executeLabel,
  executeLoading,
  executeDisabled,
  onExecute,
  children,
}: {
  command: AssetCommandDefinition;
  executeLabel: string;
  executeLoading: boolean;
  executeDisabled: boolean;
  onExecute: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation('assets');

  return (
    <div className={styles.shell}>
      <div className={styles.body}>
        <CommandHeader command={command} />
        {children}
      </div>
      <div className={styles.executeBar}>
        <Typography.Text className={styles.executeHint}>
          {t('detail.commands.executeHint')}
        </Typography.Text>
        <CommandExecuteButton
          label={executeLabel}
          loading={executeLoading}
          disabled={executeDisabled}
          onClick={onExecute}
        />
      </div>
    </div>
  );
}

function ResetCommandForm({
  command,
  execute,
  commandsLocked,
  isActive,
}: FormProps) {
  const { t } = useTranslation('assets');
  const [resetType, setResetType] = useState<AssetResetType>('Soft');

  const submit = () => {
    if (commandsLocked) {
      return;
    }
    Modal.confirm({
      title: t('detail.commands.reset.confirmTitle'),
      content: t('detail.commands.reset.confirmContent', {
        type: t(resetTypeLabelKey(resetType)),
      }),
      okText: t('detail.commands.reset.confirmOk'),
      cancelText: t('detail.commands.confirmCancel'),
      okButtonProps: { danger: resetType === 'Hard' },
      onOk: () => execute({ action: 'Reset', type: resetType }),
    });
  };

  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={commandsLocked}
      onExecute={submit}
    >
      <div>
        <Typography.Text className={styles.fieldLabel}>
          {t('detail.commands.reset.typeLabel', 'Reset type')}
        </Typography.Text>
        <div className={styles.resetOptions}>
          <Radio.Group
            value={resetType}
            disabled={commandsLocked}
            onChange={(event) =>
              setResetType(event.target.value as AssetResetType)
            }
          >
            <label className={styles.resetOption}>
              <Radio value="Soft" />
              <span className={styles.resetOptionLabel}>
                {t('detail.commands.soft')}
              </span>
            </label>
            <label className={styles.resetOption}>
              <Radio value="Hard" />
              <span className={styles.resetOptionLabel}>
                {t('detail.commands.hard')}
              </span>
            </label>
          </Radio.Group>
        </div>
      </div>
    </CommandFormShell>
  );
}

function SimpleCommandForm({
  command,
  execute,
  commandsLocked,
  isActive,
}: FormProps) {
  const { t } = useTranslation('assets');
  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={commandsLocked}
      onExecute={() => {
        if (!commandsLocked) {
          void execute({ action: 'GetLocalListVersion' });
        }
      }}
    />
  );
}

function ConfirmOnlyCommandForm({
  command,
  execute,
  commandsLocked,
  isActive,
  confirmKind,
}: FormProps & { confirmKind: 'clearCache' }) {
  const { t } = useTranslation('assets');
  const submit = () => {
    if (commandsLocked) {
      return;
    }
    Modal.confirm({
      title: t(`detail.commands.${confirmKind}.confirmTitle`),
      content: t(`detail.commands.${confirmKind}.confirmContent`),
      okText: t(`detail.commands.${confirmKind}.confirmOk`),
      cancelText: t('detail.commands.confirmCancel'),
      onOk: () => execute({ action: 'ClearCache' }),
    });
  };

  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={commandsLocked}
      onExecute={submit}
    />
  );
}

function GetConfigurationForm({
  command,
  execute,
  commandsLocked,
  isActive,
}: FormProps) {
  const { t } = useTranslation('assets');
  const [keys, setKeys] = useState<AssetGetConfigurationKey[]>([
    'HeartbeatInterval',
  ]);

  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={commandsLocked || keys.length === 0}
      onExecute={() => {
        if (!commandsLocked && keys.length > 0) {
          void execute({ action: 'GetConfiguration', keys });
        }
      }}
    >
      <Typography.Text type="secondary">
        {t('detail.commands.getConfiguration.keysLabel')}
      </Typography.Text>
      <Checkbox.Group
        disabled={commandsLocked}
        value={keys}
        onChange={(values) => setKeys(values as AssetGetConfigurationKey[])}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        options={GET_CONFIGURATION_KEYS.map((key) => ({
          label: t(configurationKeyLabelKey(key)),
          value: key,
        }))}
      />
    </CommandFormShell>
  );
}

function TriggerMessageForm({
  command,
  execute,
  commandsLocked,
  isActive,
}: FormProps) {
  const { t } = useTranslation('assets');
  const [requestedMessage, setRequestedMessage] =
    useState<AssetTriggerMessage>('StatusNotification');

  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={commandsLocked}
      onExecute={() => {
        if (!commandsLocked) {
          void execute({
            action: 'TriggerMessage',
            requestedMessage,
          });
        }
      }}
    >
      <Typography.Text type="secondary">
        {t('detail.commands.triggerMessage.requestedMessageLabel')}
      </Typography.Text>
      <Select
        disabled={commandsLocked}
        value={requestedMessage}
        onChange={(value) => setRequestedMessage(value)}
        options={TRIGGER_MESSAGES.map((message) => ({
          value: message,
          label: t(`detail.commands.triggerMessage.options.${message}`),
        }))}
        style={{ maxWidth: 360, width: '100%' }}
      />
    </CommandFormShell>
  );
}

function GetDiagnosticsForm({
  command,
  execute,
  commandsLocked,
  isActive,
}: FormProps) {
  const { t } = useTranslation('assets');
  const [retries, setRetries] = useState<number | null>(null);
  const [retryInterval, setRetryInterval] = useState<number | null>(null);
  const [windowRange, setWindowRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);

  const submit = () => {
    if (commandsLocked) {
      return;
    }
    const start = windowRange?.[0];
    const stop = windowRange?.[1];
    void execute({
      action: 'GetDiagnostics',
      ...(retries != null ? { retries } : {}),
      ...(retryInterval != null ? { retryInterval } : {}),
      ...(start != null ? { startTime: start.toISOString() } : {}),
      ...(stop != null ? { stopTime: stop.toISOString() } : {}),
    });
  };

  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={commandsLocked}
      onExecute={submit}
    >
      <Typography.Text type="secondary">
        {t('detail.commands.getDiagnostics.optionalFieldsHint')}
      </Typography.Text>
      <Flex gap="middle" wrap="wrap">
        <div>
          <Typography.Text type="secondary">
            {t('detail.commands.getDiagnostics.retriesLabel')}
          </Typography.Text>
          <div>
            <InputNumber
              min={0}
              max={10}
              precision={0}
              disabled={commandsLocked}
              value={retries}
              onChange={(value) =>
                setRetries(typeof value === 'number' ? value : null)
              }
              style={{ width: 140 }}
            />
          </div>
        </div>
        <div>
          <Typography.Text type="secondary">
            {t('detail.commands.getDiagnostics.retryIntervalLabel')}
          </Typography.Text>
          <div>
            <InputNumber
              min={0}
              max={3600}
              precision={0}
              disabled={commandsLocked}
              value={retryInterval}
              onChange={(value) =>
                setRetryInterval(typeof value === 'number' ? value : null)
              }
              addonAfter={t('detail.commands.getDiagnostics.secondsUnit')}
              style={{ width: 180 }}
            />
          </div>
        </div>
      </Flex>
      <div>
        <Typography.Text type="secondary">
          {t('detail.commands.getDiagnostics.windowLabel')}
        </Typography.Text>
        <div>
          <DatePicker.RangePicker
            showTime
            disabled={commandsLocked}
            value={windowRange}
            onChange={(value) =>
              setWindowRange(value as [Dayjs | null, Dayjs | null] | null)
            }
            style={{ maxWidth: 420, width: '100%' }}
          />
        </div>
      </div>
    </CommandFormShell>
  );
}

function UpdateHeartbeatForm({
  command,
  execute,
  commandsLocked,
  isActive,
  heartbeatInterval,
}: FormProps) {
  const { t } = useTranslation('assets');
  const [form] = Form.useForm<{ value: number | null }>();
  const watchedValue = Form.useWatch('value', form);
  const valueString =
    watchedValue != null && Number.isInteger(watchedValue)
      ? String(watchedValue)
      : '';
  const canSubmit =
    !commandsLocked && isValidOcppHeartbeatIntervalValue(valueString);

  useEffect(() => {
    if (
      heartbeatInterval &&
      isPositiveIntegerString(heartbeatInterval) &&
      form.getFieldValue('value') == null
    ) {
      form.setFieldsValue({ value: Number(heartbeatInterval) });
    }
  }, [form, heartbeatInterval]);

  const submit = async () => {
    if (!canSubmit) {
      return;
    }

    try {
      const { value } = await form.validateFields();
      if (value == null || !Number.isInteger(value)) {
        return;
      }
      const nextValue = String(value);
      if (!isValidOcppHeartbeatIntervalValue(nextValue)) {
        return;
      }

      Modal.confirm({
        title: t('detail.commands.updateHeartbeat.confirmTitle'),
        content: t('detail.commands.updateHeartbeat.confirmContent', {
          current:
            heartbeatInterval ??
            t('detail.commands.updateHeartbeat.currentUnknown'),
          next: nextValue,
        }),
        okText: t('detail.commands.updateHeartbeat.confirmOk'),
        cancelText: t('detail.commands.confirmCancel'),
        onOk: async () => {
          await execute({
            action: 'ChangeConfiguration',
            key: 'HeartbeatInterval',
            value: nextValue,
          });
        },
      });
    } catch {
      // Ant Design Form surfaces field errors; do not proceed.
    }
  };

  return (
    <CommandFormShell
      command={command}
      executeLabel={t(command.submitKey)}
      executeLoading={isActive}
      executeDisabled={!canSubmit}
      onExecute={() => {
        void submit();
      }}
    >
      <Typography.Text>
        {t('detail.commands.updateHeartbeat.currentLabel')}:{' '}
        {heartbeatInterval
          ? t('detail.commands.updateHeartbeat.seconds', {
              value: heartbeatInterval,
            })
          : t('detail.commands.updateHeartbeat.currentUnknown')}
      </Typography.Text>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={commandsLocked}
        initialValues={{ value: null }}
      >
        <Form.Item
          label={t('detail.commands.updateHeartbeat.newLabel')}
          name="value"
          style={{ marginBottom: 0 }}
          rules={[
            {
              required: true,
              message: t('detail.commands.updateHeartbeat.rangeError', {
                min: OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS,
                max: OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS,
              }),
            },
            {
              validator: async (_, value: number | null) => {
                if (value == null) {
                  return;
                }
                if (
                  !Number.isInteger(value) ||
                  !isValidOcppHeartbeatIntervalValue(String(value))
                ) {
                  throw new Error(
                    t('detail.commands.updateHeartbeat.rangeError', {
                      min: OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS,
                      max: OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS,
                    }),
                  );
                }
              },
            },
          ]}
        >
          <InputNumber
            min={OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS}
            max={OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS}
            step={1}
            precision={0}
            addonAfter={t('detail.commands.updateHeartbeat.unit')}
            style={{ width: 200 }}
          />
        </Form.Item>
        {valueString && !isValidOcppHeartbeatIntervalValue(valueString) ? (
          <Typography.Text
            type="danger"
            style={{ display: 'block', marginTop: 4 }}
          >
            {t('detail.commands.updateHeartbeat.rangeError', {
              min: OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS,
              max: OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS,
            })}
          </Typography.Text>
        ) : null}
      </Form>
      <Typography.Text type="warning">
        {t('detail.commands.updateHeartbeat.warning')}
      </Typography.Text>
    </CommandFormShell>
  );
}
